<template>
  <div class="page">
    <div class="container py-3 py-lg-4">
      <div class="row justify-content-center">
        <div class="col-lg-10 col-xl-9">
          <div class="calendar-shell">
            <p class="eyebrow">Events</p>
            <h1>Upcoming Schedule</h1>
            <p class="lead">
              This calendar view is now built directly into the site so it works reliably for visitors.
            </p>

            <div class="calendar-card">
              <div class="calendar-header">
                <div>
                  <p class="month-label">{{ monthLabel }}</p>
                  <h2>{{ currentYear }}</h2>
                </div>
                <a
                  href="https://calendar.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="calendar-link"
                >
                  Open Google Calendar
                </a>
              </div>

              <div class="calendar-grid">
                <div v-for="dayName in weekdayLabels" :key="dayName" class="day-name">
                  {{ dayName }}
                </div>
                <div
                  v-for="day in calendarDays"
                  :key="day.key"
                  class="day-cell"
                  :class="{ muted: !day.inMonth, today: day.isToday, event: day.events.length }"
                >
                  <span class="date-number">{{ day.date }}</span>
                  <span v-if="day.events.length" class="event-dot"></span>
                </div>
              </div>
            </div>

            <div class="events-list">
              <div v-for="event in upcomingEvents" :key="event.title" class="event-item">
                <div class="event-date">{{ formatDate(event.date) }}</div>
                <div class="event-copy">
                  <strong>{{ event.title }}</strong>
                  <p>{{ event.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Events",
  data() {
    return {
      currentDate: new Date(),
      weekdayLabels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      events: [
        { title: "Website consultation", description: "Discuss goals and project scope.", date: "2026-07-18" },
        { title: "Design review", description: "Review layout and branding direction.", date: "2026-07-25" },
        { title: "Client kickoff", description: "Launch the next website build phase.", date: "2026-08-01" },
      ],
    };
  },
  computed: {
    monthLabel() {
      return this.currentDate.toLocaleString("en-US", { month: "long" });
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startOffset = firstDay.getDay();
      const totalDays = lastDay.getDate();
      const cells = [];

      for (let i = 0; i < startOffset; i += 1) {
        cells.push({ key: `prev-${i}`, date: "", inMonth: false, events: [] });
      }

      for (let day = 1; day <= totalDays; day += 1) {
        const date = new Date(year, month, day);
        const key = date.toISOString().slice(0, 10);
        const events = this.events.filter((event) => event.date === key);

        cells.push({
          key,
          date: day,
          inMonth: true,
          isToday: this.isSameDay(date, new Date()),
          events,
        });
      }

      while (cells.length % 7 !== 0) {
        cells.push({ key: `next-${cells.length}`, date: "", inMonth: false, events: [] });
      }

      return cells;
    },
    upcomingEvents() {
      return this.events
        .slice()
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 3);
    },
  },
  methods: {
    isSameDay(left, right) {
      return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.page {
  position: relative;
  min-height: 100vh;
  background: #0b1020;
  overflow: hidden;
}

.page::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: url("../assets/screen-5.png");
  background-size: cover;
  background-position: center;
  filter: blur(4px) brightness(0.7);
  transform: scale(1.08);
  z-index: 0;
}

.page > * {
  position: relative;
  z-index: 1;
}

.calendar-shell {
  background: rgba(7, 17, 31, 0.9);
  border-radius: 18px;
  padding: 16px 18px;
  color: white;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.25);
}
.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.7rem;
  color: #c5d4ff;
  margin-bottom: 6px;
}
h1 {
  font-size: 1.3rem;
  margin-bottom: 6px;
}
.lead {
  color: #dde7ff;
  margin-bottom: 12px;
  font-size: 0.95rem;
  line-height: 1.5;
}
.calendar-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 12px;
}
.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.month-label {
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.7rem;
  color: #c5d4ff;
  margin: 0;
}
.calendar-header h2 {
  font-size: 1.1rem;
  margin: 2px 0 0;
}
.calendar-link {
  color: #7dd3fc;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
}
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}
.day-name {
  text-align: center;
  color: #c5d4ff;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding-bottom: 4px;
}
.day-cell {
  min-height: 42px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 6px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
}
.day-cell.muted {
  opacity: 0.45;
}
.day-cell.today {
  border-color: #7dd3fc;
  box-shadow: inset 0 0 0 1px #7dd3fc;
}
.day-cell.event {
  background: rgba(125, 211, 252, 0.16);
}
.date-number {
  font-size: 0.85rem;
}
.event-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7dd3fc;
  display: inline-block;
  margin-top: 3px;
}
.events-list {
  margin-top: 12px;
  display: grid;
  gap: 8px;
}
.event-item {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.event-item:first-child {
  border-top: 0;
  padding-top: 0;
}
.event-date {
  color: #7dd3fc;
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 58px;
}
.event-copy strong {
  display: block;
  margin-bottom: 2px;
}
.event-copy p {
  margin: 0;
  color: #dfe8ff;
  font-size: 0.9rem;
}
</style>
